import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Button, makeStyles, Dropdown, Option, Checkbox } from "@fluentui/react-components";
import {
  TextBold24Regular,
  TextItalic24Regular,
  TextUnderline24Regular,
  TextBulletListSquare24Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import LanguageDetect from "languagedetect";

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "2px",
    backgroundColor: "#f3f2f1",
    borderBottom: "1px solid #e0e0e0",
    flexWrap: "wrap",
  },
  toolbarButton: {
    minWidth: "32px",
    height: "32px",
    padding: "0 8px",
  },
  toolbarSeparator: {
    width: "1px",
    height: "24px",
    backgroundColor: "#d0d0d0",
    margin: "0 4px",
  },
  languageSelector: {
    height: "32px",
    minWidth: "120px",
  },
  editor: {
    //border: "1px solid #e0e0e0",
    minHeight: "150px",
    padding: "12px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
    fontSize: "14px",
    lineHeight: "1.5",
    overflow: "auto",
    outline: "none",
  },
  container: {
    //border: "1px solid #e0e0e0",
    borderRadius: "4px",
    overflow: "hidden",
    marginBottom: "0",
    position: "relative",
    width: "100%",
  },
  editorWrapper: {
    position: "relative",
  },
  sendButton: {
    position: "absolute",
    right: "8px",
    bottom: "8px",
    zIndex: 1,
  },
  bottomButtons: {
    position: "absolute",
    right: "8px",
    bottom: "8px",
    zIndex: 1,
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },
});

const RichTextEditor = forwardRef(
  (
    {
      value = "",
      defaultValue = "",
      onChange,
      //onSend,
      placeholder = "Enter text...",
      selectedLanguage,
      onLanguageChange,
      canSend = true,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const editorRef = useRef(null);
    const detectorRef = useRef(null);
    const [content, setContent] = useState(value || defaultValue);
    const historyRef = useRef([""]);
    const historyIndexRef = useRef(0);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      getContent: () => content,
      setContent: (newContent) => {
        setContent(newContent);
        if (editorRef.current) {
          editorRef.current.innerHTML = newContent;
        }
        onChange?.(newContent);
      },
      clear: () => {
        setContent("");
        handleLanguageChange(null, { optionValue: "" });
        if (editorRef.current) {
          editorRef.current.innerHTML = "";
        }
        historyRef.current = [""];
        historyIndexRef.current = 0;
      },
      getLanguage: () => selectedLanguage,
    }));

    if (!detectorRef.current) {
      const detector = new LanguageDetect();
      detectorRef.current = detector;
    }

    const languageOptions = [
      { value: null, label: t("generic.none") },
      { value: "en", label: "English" },
      { value: "fr", label: "Français" },
    ];

    const [autoDetect, setAutoDetect] = useState(true);

    const applyLanguage = (langCode) => {
      const next = langCode || null;
      onLanguageChange?.(next);
    };

    const handleLanguageChange = (event, data) => {
      const newLang = data.optionValue ?? "";
      if (!newLang) {
        applyLanguage("");
        return;
      }

      applyLanguage(newLang);
    };

    const selectedLabel = languageOptions.find((opt) => opt.value === selectedLanguage)?.label || "";

    useEffect(() => {
      if (!autoDetect) return;
      if (selectedLanguage) return;

      const plainText = editorRef.current?.innerText?.trim() || "";
      if (plainText.length < 25) return;

      const detector = detectorRef.current;
      if (!detector) return;

      const detection = detector.detect(plainText, 3);
      const nameToCode = {
        english: "en",
        french: "fr",
      };

      const match = detection.find(([langName]) => {
        const code = nameToCode[langName?.toLowerCase?.() || ""];
        return code && languageOptions.some((opt) => opt.value === code);
      });

      if (match) {
        const detectedCode = nameToCode[match[0].toLowerCase()];
        if (detectedCode) {
          applyLanguage(detectedCode);
        }
      }
    }, [content, selectedLanguage, autoDetect]);

    const executeCommand = (command, value = null) => {
      document.execCommand(command, false, value);
      editorRef.current?.focus();
    };

    const handleInput = (e) => {
      const newContent = e.currentTarget.innerHTML;
      setContent(newContent);
      onChange?.(newContent);

      // Add to history
      if (historyRef.current[historyIndexRef.current] !== newContent) {
        historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
        historyRef.current.push(newContent);
        historyIndexRef.current += 1;
      }
    };

    const handleUndo = () => {
      if (historyIndexRef.current > 0) {
        historyIndexRef.current -= 1;
        const previousContent = historyRef.current[historyIndexRef.current];
        setContent(previousContent);
        editorRef.current.innerHTML = previousContent;
        onChange?.(previousContent);
      }
    };

    const handleRedo = () => {
      if (historyIndexRef.current < historyRef.current.length - 1) {
        historyIndexRef.current += 1;
        const nextContent = historyRef.current[historyIndexRef.current];
        setContent(nextContent);
        editorRef.current.innerHTML = nextContent;
        onChange?.(nextContent);
      }
    };

    useEffect(() => {
      if (editorRef.current && value && editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
        setContent(value);
      }
    }, [value]);

    return (
      <div id="rich-text-editor" className={styles.container}>
        <div className={styles.toolbar}>
          <Button
            className={styles.toolbarButton}
            appearance="subtle"
            size="small"
            icon={<TextBold24Regular />}
            title={t("editor.bold")}
            onClick={() => executeCommand("bold")}
          />
          <Button
            className={styles.toolbarButton}
            appearance="subtle"
            size="small"
            icon={<TextItalic24Regular />}
            title={t("editor.italic")}
            onClick={() => executeCommand("italic")}
          />
          <Button
            className={styles.toolbarButton}
            appearance="subtle"
            size="small"
            icon={<TextUnderline24Regular />}
            title={t("editor.underline")}
            onClick={() => executeCommand("underline")}
          />
          <Button
            className={styles.toolbarButton}
            appearance="subtle"
            size="small"
            icon={<TextBulletListSquare24Regular />}
            title={t("editor.bulletList")}
            onClick={() => executeCommand("insertUnorderedList")}
          />
          <Button
            className={styles.toolbarButton}
            appearance="subtle"
            size="small"
            title={t("editor.numberedList")}
            onClick={() => executeCommand("insertOrderedList")}
          >
            1.
          </Button>
          <Button
            className={styles.toolbarButton}
            appearance="subtle"
            size="small"
            title={t("editor.undo")}
            onClick={handleUndo}
            disabled={historyIndexRef.current === 0}
          >
            ↶
          </Button>
          <Button
            className={styles.toolbarButton}
            appearance="subtle"
            size="small"
            title={t("editor.redo")}
            onClick={handleRedo}
            disabled={historyIndexRef.current === historyRef.current.length - 1}
          >
            ↷
          </Button>
          <div className={styles.toolbarSeparator} />
          <Dropdown
            className={styles.languageSelector}
            value={selectedLabel}
            selectedOptions={selectedLanguage === "" || !selectedLanguage ? [] : [selectedLanguage]}
            onOptionSelect={handleLanguageChange}
            appearance="underlined"
          >
            {languageOptions.map((opt) => (
              <Option key={opt.value || "none"} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Dropdown>
          {!selectedLanguage && (
            <Checkbox
              label={t("nounPhrases.detectLanguage")}
              checked={autoDetect}
              onChange={(ev, data) => setAutoDetect(data.checked)}
            />
          )}
        </div>
        <div className={styles.editorWrapper}>
          <div
            ref={editorRef}
            className={styles.editor}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            data-placeholder={placeholder}
            style={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
            }}
          />
        </div>
      </div>
    );
  },
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
